import type { ReactNode } from "react";
import { Icon } from "../components/icon";
import { Skeleton } from "../components/states";
import type { IconName } from "../icons";
import { cn } from "../lib/cn";
import { cssVars } from "../css-vars";
import { AppLink } from "./link";

export function NavSectionLabel({ children }: { children: ReactNode }) {
  return <div className="nav-section">{children}</div>;
}

export interface NavItemProps {
  href: string;
  icon: IconName;
  label: string;
  active?: boolean;
  badge?: string | number;
  badgeTone?: "brand" | "accent";
  /** Screen-reader context for the badge, e.g. "3 open conversations". */
  badgeLabel?: string;
  /** Plan-locked link: stays visible, shows a lock and announces `lockedLabel` ("Available on Standard"). */
  locked?: boolean;
  lockedLabel?: string;
}

/** `.nav-item` link; `aria-current="page"` draws the active brand rail. */
export function NavItem({ href, icon, label, active = false, badge, badgeTone = "brand", badgeLabel, locked = false, lockedLabel }: NavItemProps) {
  return (
    <AppLink href={href} className="nav-item" aria-current={active ? "page" : undefined}>
      <Icon name={icon} />
      <span className="truncate">{label}</span>
      {locked ? (
        <span className="ml-auto inline-flex items-center text-chrome-muted" data-testid="nav-lock">
          <Icon name="lock" className="size-3.5" />
          {lockedLabel ? <span className="sr-only">{lockedLabel}</span> : null}
        </span>
      ) : null}
      {badge !== undefined ? (
        <span
          className={cn(
            "ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-pill px-1.5 text-[10px] font-bold text-ink-950",
            badgeTone === "accent" ? "bg-signal-500" : "bg-brand-500",
          )}
        >
          <span aria-hidden={badgeLabel ? true : undefined}>{badge}</span>
          {badgeLabel ? <span className="sr-only">{badgeLabel}</span> : null}
        </span>
      ) : null}
    </AppLink>
  );
}

/** `warning` / `danger` pair their colour with `status` text and an icon (never colour alone). */
export type UsageTone = "normal" | "warning" | "danger";

const CARD_TONE: Record<UsageTone, string> = {
  normal: "border-chrome-line",
  warning: "border-signal-500/60",
  danger: "border-danger-fill",
};
const BAR_TONE: Record<UsageTone, string> = {
  normal: "bg-brand-500",
  warning: "bg-signal-500",
  danger: "bg-danger-fill",
};
const STATUS_ICON: Record<Exclude<UsageTone, "normal">, { name: IconName; icon: string; text: string }> = {
  warning: { name: "alert-triangle", icon: "text-signal-300", text: "text-signal-300" },
  danger: { name: "x-circle", icon: "text-danger-fill", text: "text-white" },
};

export interface UsageMeterProps {
  href: string;
  label: string;
  used: number;
  /** `null` = unlimited: no bar is drawn. */
  limit: number | null;
  /** Already-formatted "6,240 / 10,000". */
  valueText: string;
  footnote: ReactNode;
  tone?: UsageTone;
  /** Visible explanation of a `warning` / `danger` tone, e.g. "Limit reached". */
  status?: string;
}

/** Share of the bar to fill: an allowance of 0 counts as exhausted. */
function barPercent(used: number, limit: number): number {
  if (limit <= 0) return 100;
  return Math.min(100, Math.round((used / limit) * 100));
}

/** Sidebar "Messages this month" meter (width via the `--geo-width` custom property). */
export function UsageMeter({ href, label, used, limit, valueText, footnote, tone = "normal", status }: UsageMeterProps) {
  const statusStyle = tone === "normal" || !status ? null : STATUS_ICON[tone];
  return (
    <AppLink
      href={href}
      data-tone={tone}
      className={cn("block rounded-md border bg-chrome-2 p-3 transition-colors hover:bg-chrome-3", CARD_TONE[tone])}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 text-[11px] font-semibold">
        <span className="text-chrome-muted">{label}</span>
        {/* With a bar, the same text is its `aria-valuetext`: read once, in the link's name. */}
        <span aria-hidden={limit === null ? undefined : true} className="tabular-nums text-white">
          {valueText}
        </span>
      </div>
      {limit === null ? null : (
        <div
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-valuenow={Math.min(used, limit)}
          aria-valuetext={valueText}
          className="mt-2 h-1.5 rounded-pill bg-chrome-line"
        >
          <div className={cn("h-1.5 w-[var(--geo-width)] rounded-pill", BAR_TONE[tone])} style={cssVars({ "--geo-width": `${String(barPercent(used, limit))}%` })} />
        </div>
      )}
      {statusStyle ? (
        <div className={cn("mt-2 flex items-center gap-1 text-[11px] font-bold", statusStyle.text)}>
          <Icon name={statusStyle.name} className={cn("size-3.5", statusStyle.icon)} />
          <span>{status}</span>
        </div>
      ) : null}
      <div className="mt-2 text-[11px] text-chrome-muted">{footnote}</div>
    </AppLink>
  );
}

/** Same-size placeholder while the usage numbers load. */
export function UsageMeterSkeleton() {
  return (
    <div aria-hidden="true" data-testid="usage-meter-skeleton" className="rounded-md border border-chrome-line bg-chrome-2 p-3">
      <Skeleton className="h-4 w-full bg-chrome-3" />
      <Skeleton className="mt-2 h-1.5 w-full rounded-pill bg-chrome-3" />
      <Skeleton className="mt-2 h-4 w-2/3 bg-chrome-3" />
    </div>
  );
}
